import React, { useState, useEffect } from 'react';
import useDebounce from '../../hooks/useDebounce';
import { Eye, ChevronLeft, ChevronRight, Search, X, Plus, Edit2 } from 'lucide-react';
import DateFilter from '../../components/DateFilter';
import { isDateInRange, formatAppDate, formatAppDateOnly, formatAppTime } from '../../utils/dateUtils';
import Dropdown from '../../components/Dropdown';
// Reusing AddLead component for the view modal
import AddLead from '../leads/AddLead';
import AddCustomerModal from './AddCustomerModal';
import { useAuth } from '../../context/AuthContext';

const CustomerList = () => {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  
  // Pagination & Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('this_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const { default: api } = await import('../../utils/api');
      
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        isCustomer: 'true'
      });
      
      if (debouncedSearchTerm) queryParams.append('search', debouncedSearchTerm);
      if (dateFilter && dateFilter !== 'all') queryParams.append('dateFilter', dateFilter);
      if (customStartDate) queryParams.append('startDate', customStartDate);
      if (customEndDate) queryParams.append('endDate', customEndDate);
      
      const res = await api.get(`/leads?${queryParams.toString()}`);
      let dataToFormat = res.data.data || res.data;
      
      const convertedLeads = dataToFormat.map(lead => ({
        id: lead.id,
        name: lead.companyName,
        companyName: lead.companyName,
        contact: lead.contactPerson,
        contactPerson: lead.contactPerson,
        phone: lead.phone,
        email: lead.email,
        contactPhone: lead.contactPhone,
        contactEmail: lead.contactEmail,
        frontCard: lead.frontCard,
        backCard: lead.backCard,
        address: lead.address,
        address2: lead.address2,
        city: lead.city,
        state: lead.state,
        pincode: lead.pincode,
        agencyType: lead.agencyType,
        otherAgencyType: lead.otherAgencyType || '',
        machine: lead.machine?.name ? `${lead.machine.name}${lead.machineModel ? ' - ' + lead.machineModel : ''}` : 'Unknown',
        machineBrand: lead.machine?.name || '',
        machineId: lead.machineId || lead.machine?.id || null,
        machineModel: lead.machineModel || '',
        type: lead.interestType || lead.leadType?.name || (lead.otherInterest ? `Other (${lead.otherInterest})` : 'Unknown'),
        interestType: lead.interestType || lead.leadType?.name || '',
        interestedType: lead.interestType || lead.leadType?.name || '',
        machineType: lead.machineType || '',
        leadType: lead.leadType,
        leadTypeId: lead.leadTypeId || lead.leadType?.id || null,
        otherInterest: lead.otherInterest || '',
        assignee: lead.user?.name || 'Unassigned',
        status: lead.status?.name || 'Completed',
        source: lead.leadSource?.name || '-',
        createdAt: lead.createdAt,
        createdBy: lead.createdBy || 'Unknown',
        notes: lead.notes,
        followups: lead.followups || [],
        conversionDate: lead.updatedAt
      }));
      setCustomers(convertedLeads);
      
      if (res.data.data) {
        setTotalPages(res.data.totalPages || 1);
        setTotalCustomers(res.data.total || 0);
      } else {
        setTotalPages(1);
        setTotalCustomers(res.data.length);
      }
    } catch (error) {
      console.error('Failed to fetch customers', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, itemsPerPage, debouncedSearchTerm, dateFilter, customStartDate, customEndDate]);

  const handleView = (customer) => {
    setViewingCustomer(customer);
    setShowModal(true);
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setShowAddCustomerModal(true);
  };

  const handleAddNew = () => {
    setEditingCustomer(null);
    setShowAddCustomerModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setViewingCustomer(null);
  };

  // Filter Logic
  const userRole = (user?.role || '').toLowerCase();
  const canViewAssignee = !userRole.includes('market') && !userRole.includes('delivery');
  const roleFilteredCustomers = user?.role !== 'Admin' ? customers.filter(c => c.assignee === user?.name || c.createdBy === user?.name) : customers;

  const paginatedCustomers = roleFilteredCustomers;

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const canAddCustomer = user?.role === 'Admin' || 
                         user?.permissions?.includes('customers:write') || 
                         user?.permissions?.includes('leads:write');

  return (
    <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold">Customers</h2>
          <p className="text-muted mt-1 text-sm">View details of leads that have successfully converted into customers.</p>
        </div>
        {canAddCustomer && (
          <button 
            className="btn btn-primary flex items-center gap-2 whitespace-nowrap"
            style={{ padding: '0.5rem 1.15rem', fontSize: '0.85rem', borderRadius: '8px' }}
            onClick={handleAddNew}
          >
            <Plus size={16} />
            <span>Add Customer</span>
          </button>
        )}
      </div>

      <div className="card">
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 mb-3">
          <div className="relative flex-1" style={{ maxWidth: '380px', minWidth: '220px' }}>
            <Search size={15} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', pointerEvents: 'none' }} />
            <input 
              type="text" 
              placeholder="Search customers..." 
              className="input-field" 
              style={{ paddingLeft: '2.25rem', paddingRight: searchTerm ? '2rem' : '0.75rem', height: '36px', fontSize: '0.825rem', width: '100%', borderRadius: '8px' }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')} 
                style={{ position: 'absolute', right: '0.65rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                title="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <DateFilter 
              value={dateFilter} 
              onChange={(val) => {
                setDateFilter(val);
                setCurrentPage(1);
              }} 
              style={{ minWidth: '135px' }} 
            />
          </div>
        </div>

        <div className="table-responsive">
          <table style={{ width: '100%', minWidth: '760px', borderCollapse: 'collapse', textAlign: 'left', marginTop: 10 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.775rem' }}>Conversion Date</th>
                <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.775rem' }}>Company Name</th>
                <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.775rem' }}>Contact Person</th>
                <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.775rem' }}>Phone</th>
                <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.775rem' }}>Source</th>
                {canViewAssignee && (
                  <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.775rem' }}>Assigned To</th>
                )}
                <th style={{ padding: '0.65rem 0.75rem', textAlign: 'center', fontSize: '0.775rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCustomers.length > 0 ? paginatedCustomers.map(customer => (
                <tr key={customer.id} className="hover-row" style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.8rem' }}>
                    <div style={{ fontWeight: '500', color: 'var(--color-text-main)', whiteSpace: 'nowrap' }}>
                      {formatAppDateOnly(customer.conversionDate)}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                      {formatAppTime(customer.conversionDate)}
                    </div>
                  </td>
                  <td style={{ padding: '0.65rem 0.75rem', fontWeight: '500', color: 'var(--color-text-main)', fontSize: '0.825rem' }}>{customer.name}</td>
                  <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.825rem' }}>{customer.contact}</td>
                  <td style={{ padding: '0.65rem 0.75rem', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>
                    {customer.phone ? (
                      <div>
                        <a href={`tel:${customer.phone}`} style={{ color: 'var(--color-primary)', textDecoration: 'none', fontWeight: '500' }}>{customer.phone}</a>
                      </div>
                    ) : (customer.contactPhone && (
                      <div>
                        <a href={`tel:${customer.contactPhone}`} style={{ color: 'var(--color-primary)', textDecoration: 'none', fontWeight: '500' }}>{customer.contactPhone}</a>
                      </div>
                    ))}
                    {(customer.email || customer.contactEmail) && (
                      <div className="text-xs mt-0.5" style={{ wordBreak: 'break-all' }}>
                        <a href={`mailto:${customer.email || customer.contactEmail}`} style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}>{customer.email || customer.contactEmail}</a>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.65rem 0.75rem', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>{customer.source && customer.source !== 'Unknown' ? customer.source : '-'}</td>
                  {canViewAssignee && (
                    <td style={{ padding: '0.65rem 0.75rem', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>{customer.assignee}</td>
                  )}
                  <td style={{ padding: '0.65rem 0.75rem', textAlign: 'center' }}>
                    <div className="flex justify-center gap-2">
                      <button className="btn text-primary" title="View Details" onClick={() => handleView(customer)} style={{ padding: '0.3rem' }}>
                        <Eye size={18} />
                      </button>
                      {canAddCustomer && (
                        <button 
                          className="btn text-primary" 
                          title="Edit Customer" 
                          onClick={() => handleEdit(customer)} 
                          style={{ padding: '0.3rem' }}
                        >
                          <Edit2 size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={canViewAssignee ? 7 : 6} style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>No customers found matching your criteria.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalCustomers > 0 && (
          <div className="flex justify-between items-center mt-6 fade-in" style={{ padding: '0 0.5rem' }}>
            <div className="flex items-center gap-4 text-sm text-muted">
              <div>
                Showing <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{Math.min(currentPage * itemsPerPage, totalCustomers)}</span> of <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{totalCustomers}</span> entries
              </div>
              <div className="flex items-center gap-2">
                <span>Show:</span>
                <Dropdown 
                  style={{ width: '80px', height: '32px' }}
                  value={itemsPerPage}
                  onChange={(val) => {
                    setItemsPerPage(Number(val));
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                    { value: 50, label: '50' }
                  ]}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                className="btn" 
                style={{ padding: '0.5rem', backgroundColor: 'white', border: '1px solid var(--color-border)', color: currentPage === 1 ? 'var(--color-text-muted)' : 'var(--color-text-main)', opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', borderRadius: '8px' }}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button 
                  key={page} 
                  className={`btn ${currentPage === page ? 'btn-primary' : ''}`}
                  style={{ 
                    padding: '0.5rem 1rem', 
                    backgroundColor: currentPage === page ? 'var(--color-primary)' : 'white',
                    color: currentPage === page ? 'white' : 'var(--color-text-main)',
                    border: currentPage === page ? 'none' : '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}
              <button 
                className="btn" 
                style={{ padding: '0.5rem', backgroundColor: 'white', border: '1px solid var(--color-border)', color: currentPage === totalPages || totalPages === 0 ? 'var(--color-text-muted)' : 'var(--color-text-main)', opacity: currentPage === totalPages || totalPages === 0 ? 0.5 : 1, cursor: currentPage === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer', borderRadius: '8px' }}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && viewingCustomer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 50,
          padding: '2rem',
          animation: 'fadeIn 0.2s'
        }}>
          <div className="hide-scrollbar" style={{
            backgroundColor: 'var(--color-bg-card)',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh', overflow: 'hidden',
            borderRadius: '24px',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}>
            <AddLead 
              onClose={handleCloseModal} 
              initialData={viewingCustomer}
              isViewMode={true}
              onEdit={(lead) => {
                handleCloseModal();
                handleEdit(lead);
              }}
            />
          </div>
        </div>
      )}

      {showAddCustomerModal && (
        <AddCustomerModal
          initialData={editingCustomer}
          onClose={() => {
            setShowAddCustomerModal(false);
            setEditingCustomer(null);
          }}
          onSuccess={() => {
            fetchCustomers();
          }}
        />
      )}
    </div>
  );
};

export default CustomerList;
